import { type PayloadAction } from "@reduxjs/toolkit"
import type { ReduxState } from "model/state"
import type { DateOverride } from "../helpers/date-override"
import { createPanelSlice, type PanelRootState } from "./create-panel-slice"
import { makeTemplateChartReducers, type TemplateChartSettings } from "./template-chart-settings"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyValue = any // used only for datagridState and groupBy (complex opaque shapes)

export interface AssetViewState {
  view: string
  groupBy: AnyValue // complex taxonomy object — shape defined by IAssetViewTemplateContextValue
  selectedTableLevels: string[]
  showMetricsSelector: boolean
  showNotHeld: boolean | null
  columnOrder: string
  datagridState: AnyValue // opaque DataGrid state — shape determined by MUI DataGrid
  // Per-template chart toggles (beta, content-set, exposure, factor-sensitivity, performance, risk),
  // keyed by template name; defaults and drilldown-seed rules live at the consumer call site.
  templateCharts: Partial<Record<string, TemplateChartSettings>>
  // Per-panel date range that supersedes the dashboard's dates when present.
  dateOverride: DateOverride | null
  isPreferencesLoaded: boolean
  /** Transient (never persisted): set by clonePanelState so the duplicate's sync hook knows to
   *  push the in-memory clone to the backend once. Distinguishes a real clone from an ordinary
   *  remount of an already-loaded panel, which must not re-save. Cleared after the one-time save. */
  pendingClonePersist?: boolean
  // Session-only "duplicate from view" flow (APP-2025): tracks the source view/template the
  // metrics selector was opened from so the previous view can be restored on close-without-save.
  // Never persisted (absent from ASSET_VIEW_PREFERENCE_FIELDS) and reset on clone. Lives in the
  // slice — not component state — so consumers read it via useAssetViewData without a context.
  duplicateFromViewName: string | null
  duplicateFromTemplateId: string | null
  duplicateFromViewMetrics: AnyValue
  duplicateReturnViewId: string | null
}

/** Keyed by dashboardId at the top, panelId at the inner level. */
export type AssetViewRootState = PanelRootState<AssetViewState>

/** Panel key for an AssetView rendered without a PanelIdProvider (e.g. Compare page). Also the preference subtype base. */
export const ASSET_VIEW_PREFERENCE_BASE = "asset-view"

/** Keys persisted to the preferences API and accepted by setPreferences. Narrowing the
 *  reducer to this list prevents a typo'd key in a dispatch from landing on the instance. */
export const ASSET_VIEW_PREFERENCE_FIELDS = ["view", "groupBy", "selectedTableLevels", "showNotHeld", "columnOrder", "datagridState", "templateCharts", "dateOverride"] as const

/** Co-located access to a panel's raw slot (undefined when absent); callers coalesce to DEFAULT_ASSET_VIEW_STATE. */
export const selectAssetViewPanel = (
  state: ReduxState,
  dashboardId: string,
  panelKey: string,
): AssetViewState | undefined => state.dashboard.assetView[dashboardId]?.[panelKey]

export const DEFAULT_ASSET_VIEW_STATE: AssetViewState = {
  view: "summary",
  groupBy: null,
  selectedTableLevels: [],
  showMetricsSelector: false,
  showNotHeld: null,
  columnOrder: "default",
  datagridState: {},
  templateCharts: {},
  dateOverride: null,
  isPreferencesLoaded: false,
  duplicateFromViewName: null,
  duplicateFromTemplateId: null,
  duplicateFromViewMetrics: null,
  duplicateReturnViewId: null,
}

export const assetViewSlice = createPanelSlice({
  name: "dashboard/assetView",
  defaultState: DEFAULT_ASSET_VIEW_STATE,
  preferenceFields: ASSET_VIEW_PREFERENCE_FIELDS,
  freshOverrides: () => ({ selectedTableLevels: [], datagridState: {}, templateCharts: {} }),
  // Clone: drop the metrics-selector overlay, reset the duplicate-from-view flow, fresh containers.
  cloneOverrides: (sourceState) => ({
    showMetricsSelector: false,
    duplicateFromViewName: null,
    duplicateFromTemplateId: null,
    duplicateFromViewMetrics: null,
    duplicateReturnViewId: null,
    selectedTableLevels: [...sourceState.selectedTableLevels],
    datagridState: sourceState.datagridState ? { ...sourceState.datagridState } : {},
    templateCharts: { ...sourceState.templateCharts },
  }),
  reducers: ({ ensure, initInstance }) => ({
    ...makeTemplateChartReducers<AssetViewState>(ensure),
    setView(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, view: string }>,
    ) {
      const { dashboardId, panelId, view } = action.payload
      ensure(state, dashboardId, panelId).view = view
    },

    // Seed the module-config view/dateOverride; no-op once the panel exists (sync omits both from its fallback).
    initFromModuleConfig(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, view?: string, dateOverride?: DateOverride | null }>,
    ) {
      const { dashboardId, panelId, view, dateOverride } = action.payload
      const overrides: Partial<AssetViewState> = {}
      if (view != null) overrides.view = view
      if (dateOverride !== undefined) overrides.dateOverride = dateOverride
      initInstance(state, dashboardId, panelId, overrides)
    },

    setDateOverride(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, dateOverride: DateOverride }>,
    ) {
      const { dashboardId, panelId, dateOverride } = action.payload
      ensure(state, dashboardId, panelId).dateOverride = dateOverride
    },

    clearDateOverride(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string }>,
    ) {
      const { dashboardId, panelId } = action.payload
      ensure(state, dashboardId, panelId).dateOverride = null
    },

    setGroupBy(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, groupBy: AnyValue }>,
    ) {
      const { dashboardId, panelId, groupBy } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      instance.groupBy = groupBy
      // Reset selectedTableLevels when groupBy changes — previous path may no longer be valid
      instance.selectedTableLevels = []
    },

    setSelectedTableLevels(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, selectedTableLevels: string[] }>,
    ) {
      const { dashboardId, panelId, selectedTableLevels } = action.payload
      ensure(state, dashboardId, panelId).selectedTableLevels = selectedTableLevels
    },

    setShowNotHeld(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, showNotHeld: boolean | null }>,
    ) {
      const { dashboardId, panelId, showNotHeld } = action.payload
      ensure(state, dashboardId, panelId).showNotHeld = showNotHeld
    },

    setShowMetricsSelector(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, showMetricsSelector: boolean }>,
    ) {
      const { dashboardId, panelId, showMetricsSelector } = action.payload
      ensure(state, dashboardId, panelId).showMetricsSelector = showMetricsSelector
    },

    setColumnOrder(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, columnOrder: string }>,
    ) {
      const { dashboardId, panelId, columnOrder } = action.payload
      ensure(state, dashboardId, panelId).columnOrder = columnOrder
    },

    setDatagridState(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, datagridState: AnyValue }>,
    ) {
      const { dashboardId, panelId, datagridState } = action.payload
      ensure(state, dashboardId, panelId).datagridState = datagridState
    },

    // ----- Duplicate-from-view flow (session-only; see field docs above) -----
    setDuplicateFromViewName(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, duplicateFromViewName: string | null }>,
    ) {
      const { dashboardId, panelId, duplicateFromViewName } = action.payload
      ensure(state, dashboardId, panelId).duplicateFromViewName = duplicateFromViewName
    },

    setDuplicateFromTemplateId(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, duplicateFromTemplateId: string | null }>,
    ) {
      const { dashboardId, panelId, duplicateFromTemplateId } = action.payload
      ensure(state, dashboardId, panelId).duplicateFromTemplateId = duplicateFromTemplateId
    },

    setDuplicateFromViewMetrics(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, duplicateFromViewMetrics: AnyValue }>,
    ) {
      const { dashboardId, panelId, duplicateFromViewMetrics } = action.payload
      ensure(state, dashboardId, panelId).duplicateFromViewMetrics = duplicateFromViewMetrics
    },

    setDuplicateReturnViewId(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, duplicateReturnViewId: string | null }>,
    ) {
      const { dashboardId, panelId, duplicateReturnViewId } = action.payload
      ensure(state, dashboardId, panelId).duplicateReturnViewId = duplicateReturnViewId
    },

    /** Resets all duplicate-from-view session fields for a panel (end of the flow). */
    clearDuplicateState(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string }>,
    ) {
      const { dashboardId, panelId } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      instance.duplicateFromViewName = null
      instance.duplicateFromTemplateId = null
      instance.duplicateFromViewMetrics = null
      instance.duplicateReturnViewId = null
    },
  }),
})

export default assetViewSlice.reducer
